import React, { useState } from 'react';
import { Fingerprint, User, AlertCircle, Loader2, Check, ChevronDown } from 'lucide-react';
import { useAuth, useWebAuthnSupport } from '../../hooks/useAuth';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

interface AuthFormProps {
  className?: string;
}

const AuthForm: React.FC<AuthFormProps> = ({ className = '' }) => {
  const [username, setUsername] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showUsernameForm, setShowUsernameForm] = useState(false);
  const [registerMode, setRegisterMode] = useState(false);

  const { register, login, error, clearError, isLoading } = useAuth();
  const { isSupported: webAuthnSupported, isLoading: checkingSupport } = useWebAuthnSupport();
  const { flags } = useFeatureFlags();

  const busy = isLoading || isSubmitting;

  const onSuccess = () => {
    setShowSuccess(true);
    setUsername('');
    setTimeout(() => setShowSuccess(false), 2000);
  };

  const handleUsernamelessLogin = async () => {
    setIsSubmitting(true);
    clearError();
    try {
      const ok = await login();
      if (ok) onSuccess();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUsernameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;
    setIsSubmitting(true);
    clearError();
    try {
      const ok = registerMode
        ? await register(username.trim())
        : await login(username.trim());
      if (ok) onSuccess();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (checkingSupport) {
    return (
      <div className={`max-w-sm mx-auto p-8 bg-white rounded-2xl shadow-lg ${className}`}>
        <div className="flex items-center justify-center py-8 gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <span className="text-gray-500 text-sm">Checking passkey support…</span>
        </div>
      </div>
    );
  }

  if (!webAuthnSupported) {
    return (
      <div className={`max-w-sm mx-auto p-8 bg-white rounded-2xl shadow-lg ${className}`}>
        <div className="text-center py-6">
          <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Browser not supported</h2>
          <p className="text-gray-500 text-sm">
            Use Safari 14+, Chrome 67+, or Firefox 60+ to access passkeys.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`max-w-sm mx-auto ${className}`}>
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">

        {/* Header */}
        <div className="px-8 pt-7 pb-5 text-center">
          <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md">
            <Fingerprint className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Welcome back</h1>
          <p className="text-gray-500 text-sm mt-1">
            {flags.registration_enabled ? 'Sign in or create an account' : 'Use your passkey to sign in'}
          </p>
        </div>

        <div className="px-8 pb-7 space-y-3">

          {/* Success */}
          {showSuccess && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-green-800 text-sm">
              <Check className="h-4 w-4 flex-shrink-0" />
              Signed in successfully!
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <span className="text-red-800">{error}</span>
                <button onClick={clearError} className="ml-2 text-red-500 underline text-xs">
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Primary: usernameless passkey */}
          <button
            onClick={handleUsernamelessLogin}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2.5 py-3.5 bg-blue-600 text-white rounded-xl font-semibold text-sm sm:text-base hover:bg-blue-700 active:scale-[0.98] transition-all disabled:opacity-50 touch-manipulation shadow-sm"
          >
            {busy ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Fingerprint className="h-5 w-5" />
            )}
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <p className="text-center text-xs text-gray-400 -mt-1">
            Sign in instantly with your passkey
          </p>

          {/* Registration toggle — only when enabled */}
          {flags.registration_enabled && (
            <div className="flex rounded-xl bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => { setShowUsernameForm(true); setRegisterMode(false); }}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
                  showUsernameForm && !registerMode
                    ? 'bg-white shadow-sm text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => { setShowUsernameForm(true); setRegisterMode(true); }}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
                  showUsernameForm && registerMode
                    ? 'bg-white shadow-sm text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Register
              </button>
            </div>
          )}

          {/* Username form — collapsible (or always shown when registration active) */}
          {!flags.registration_enabled && (
            <button
              type="button"
              onClick={() => setShowUsernameForm(v => !v)}
              className="w-full flex items-center justify-center gap-1.5 py-2.5 text-sm text-gray-500 hover:text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${showUsernameForm ? 'rotate-180' : ''}`} />
              Sign in with username
            </button>
          )}

          {showUsernameForm && (
            <form onSubmit={handleUsernameSubmit} className="space-y-3 pt-1">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="Username"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  disabled={busy}
                  autoComplete="webauthn"
                />
              </div>
              <button
                type="submit"
                disabled={busy || !username.trim()}
                className="w-full py-3 bg-gray-900 text-white rounded-xl font-medium text-sm hover:bg-gray-800 active:scale-[0.98] transition-all disabled:opacity-40 touch-manipulation"
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                ) : registerMode ? (
                  'Create account'
                ) : (
                  'Continue'
                )}
              </button>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default AuthForm;
